// ReportPiesDistributionByRate.jsx // TODO: delete

import React, { useMemo } from "react";
import { Container, Typography } from "@mui/material";
import HoverPaper from "../HoverPaper";
import Colours from "../../../helpers/colours";
import EChartsReact from "echarts-for-react";

const weekEnd = ["Saturday", "Sunday"];

const METRICS = {
    "Total Price": "revenueTotal",
    "Booking Total": "bookingsTotal",
    "Room Nights": "roomNightsTotal",
};

const METRICS_TITLE = {
    "Total Price": "Revenue",
    "Booking Total": "Bookings",
    "Room Nights": "Room Nights",
};

const PIE_COLORS = [
    "#5470C6",
    "#91CC75",
    "#FAC858",
    "#EE6666",
    "#73C0DE",
    "#3BA272",
    "#FC8452",
    "#9A60B4",
    "#EA7CCC",
    "#6E7074",
    "#546570",
    "#C4CCD3",
];

export const REPORT_PIES_DISTRIBUTION_BY_RATE =
    "REPORT_PIES_DISTRIBUTION_BY_RATE";
const ReportPiesDistributionByRate = ({ data, js, metric }) => {
    const groupedData = useMemo(
        () =>
            Object.values(data || {}).reduce(
                (acc, item) => {
                    const keyMap = Object.fromEntries(item.keys || []);
                    const rate = keyMap.OfferTitle || "Unknown";
                    const value = item.result?.[METRICS[metric]] || 0;

                    const day = new Date(
                        Date.UTC(
                            Number(keyMap.Year),
                            Number(keyMap.Month) - 1,
                            Number(keyMap.Day),
                        ),
                    ).toLocaleDateString("en-US", {
                        weekday: "long",
                        timeZone: "UTC",
                    });

                    acc.Weekly[rate] = (acc.Weekly[rate] || 0) + value;

                    if (weekEnd.includes(day)) {
                        acc.Weekend[rate] = (acc.Weekend[rate] || 0) + value;
                    }

                    return acc;
                },
                {
                    Weekly: {},
                    Weekend: {},
                },
            ),
        [data, metric],
    );

    const rates = useMemo(
        () =>
            Array.from(
                new Set([
                    ...Object.keys(groupedData.Weekly || {}),
                    ...Object.keys(groupedData.Weekend || {}),
                ]),
            ),
        [groupedData],
    );

    const colorMap = useMemo(
        () =>
            rates.reduce((acc, rate, index) => {
                acc[rate] = PIE_COLORS[index % PIE_COLORS.length];
                return acc;
            }, {}),
        [rates],
    );

    const buildSeriesData = useMemo(
        () => (source) =>
            rates
                .map((rate) => ({
                    name: rate,
                    value: source?.[rate] || 0,
                    itemStyle: {
                        color: colorMap[rate],
                    },
                }))
                .filter((item) => item.value > 0),
        [rates, colorMap],
    );

    const weeklySeriesData = useMemo(
        () => buildSeriesData(groupedData.Weekly),
        [buildSeriesData, groupedData.Weekly],
    );

    const weekendSeriesData = useMemo(
        () => buildSeriesData(groupedData.Weekend),
        [buildSeriesData, groupedData.Weekend],
    );

    const formatValue = (value) =>
        js?.numberWithCommas ? js.numberWithCommas(value) : value;

    const option = useMemo(
        () => ({
            tooltip: {
                trigger: "item",
                formatter: ({ seriesName, name, value, percent, marker }) =>
                    [
                        seriesName,
                        `${marker} ${name}: ${formatValue(value)} (${percent}%)`,
                    ].join("<br/>"),
            },
            legend: {
                bottom: 0,
                left: "center",
                data: rates,
            },
            title: [
                {
                    text: `Weekly ${METRICS_TITLE[metric]} Distribution by Rate`,
                    left: "25%",
                    top: 8,
                    textAlign: "center",
                    textStyle: {
                        fontSize: 14,
                        fontWeight: 600,
                    },
                },
                {
                    text: `Weekend ${METRICS_TITLE[metric]} Distribution by Rate`,
                    left: "75%",
                    top: 8,
                    textAlign: "center",
                    textStyle: {
                        fontSize: 14,
                        fontWeight: 600,
                    },
                },
            ],
            series: [
                {
                    name: "Weekly",
                    type: "pie",
                    radius: "58%",
                    center: ["25%", "45%"],
                    data: weeklySeriesData,
                    label: {
                        show: true,
                        formatter: "{d}%",
                    },
                    emphasis: {
                        scale: true,
                    },
                },
                {
                    name: "Weekend",
                    type: "pie",
                    radius: "58%",
                    center: ["75%", "45%"],
                    data: weekendSeriesData,
                    label: {
                        show: true,
                        formatter: "{d}%",
                    },
                    emphasis: {
                        scale: true,
                    },
                },
            ],
        }),
        [weeklySeriesData, weekendSeriesData, rates, metric],
    );

    return (
        <>
            <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                {METRICS_TITLE[metric]} Distribution by Rate
            </Typography>

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <EChartsReact
                    style={{ height: 430, width: "100%" }}
                    option={option}
                />
            </Container>
        </>
    );
};

export default ReportPiesDistributionByRate;
